import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ActivityContext = createContext(null);

// 30 minutes in milliseconds
const AWAY_TIMEOUT = 30 * 60 * 1000;

export const ActivityProvider = ({ children }) => {
    const { user, api } = useAuth();
    const lastActivityRef = useRef(Date.now());
    const isAwayRef = useRef(false);
    const timeoutRef = useRef(null);
    
    // Update status on backend
    const updateStatus = useCallback(async (newStatus) => {
        if (!user?.id || !api) return;
        
        try {
            await api.put(`/users/${user.id}`, { status: newStatus });
            console.log(`[Activity] Status changed to: ${newStatus}`);
        } catch (err) {
            console.error('[Activity] Failed to update status:', err);
        }
    }, [user?.id, api]);
    
    // Set user back to online
    const setOnline = useCallback(() => {
        if (isAwayRef.current && user?.id) {
            isAwayRef.current = false;
            updateStatus('online');
        }
        lastActivityRef.current = Date.now();
    }, [user?.id, updateStatus]);
    
    // Set user to away (abwesend)
    const setAway = useCallback(() => {
        if (!isAwayRef.current && user?.id) {
            // Only set away if current status is 'online'
            // Don't override manual statuses like 'busy', 'dnd', 'offline'
            const currentStatus = user?.status;
            if (currentStatus === 'online') {
                isAwayRef.current = true;
                updateStatus('away');
            }
        }
    }, [user?.id, user?.status, updateStatus]);
    
    // Check if user should be marked as away
    const checkActivity = useCallback(() => {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityRef.current;
        
        if (timeSinceLastActivity >= AWAY_TIMEOUT && !isAwayRef.current) {
            setAway();
        }
    }, [setAway]);
    
    // Handle any user activity
    const handleActivity = useCallback(() => {
        // If user was away, set them back to online
        if (isAwayRef.current) {
            setOnline();
        }
        lastActivityRef.current = Date.now();
    }, [setOnline]);
    
    // Setup activity listeners
    useEffect(() => {
        if (!user?.id) return;
        
        // Events that indicate user activity
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click'
        ];
        
        // Throttle activity handler to avoid too many calls
        let throttleTimeout = null;
        const throttledHandler = () => {
            if (throttleTimeout) return;
            throttleTimeout = setTimeout(() => {
                throttleTimeout = null;
                handleActivity();
            }, 1000); // Throttle to once per second
        };
        
        // Add event listeners
        activityEvents.forEach(event => {
            window.addEventListener(event, throttledHandler, { passive: true });
        });
        
        // Check activity every minute
        const intervalId = setInterval(checkActivity, 60000);
        
        // Also check on visibility change (tab becomes visible)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleActivity();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Set online when component mounts (user just logged in or opened app)
        if (user?.status === 'away') {
            setOnline();
        }
        
        // Cleanup
        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, throttledHandler);
            });
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (throttleTimeout) clearTimeout(throttleTimeout);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [user?.id, user?.status, handleActivity, checkActivity, setOnline]);
    
    return (
        <ActivityContext.Provider value={{ lastActivity: lastActivityRef.current }}>
            {children}
        </ActivityContext.Provider>
    );
};

export const useActivity = () => {
    const context = useContext(ActivityContext);
    if (!context) {
        throw new Error('useActivity must be used within an ActivityProvider');
    }
    return context;
};
